/* Fleur Elize — Atelier theme scripts. Vanilla JS, no dependencies. */
(() => {
  'use strict';

  const FE = window.FleurElize || { routes: {}, strings: {} };
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rootUrl = (FE.routes.root || '/').replace(/\/?$/, '/');

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const sectionIdOf = (el) => {
    const wrap = el.closest('.shopify-section');
    return wrap ? wrap.id.replace('shopify-section-', '') : null;
  };

  const toast = (msg) => {
    const el = $('.toast');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 2600);
  };

  /* ---------- Focus trap helpers ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ---------- Drawer base ---------- */
  let openDrawers = 0;
  class ModalDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this) || this;
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      openDrawers += 1;
      document.body.classList.add('has-drawer');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      this.release = trapFocus(this.panel);
      requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      openDrawers = Math.max(0, openDrawers - 1);
      if (!openDrawers) document.body.classList.remove('has-drawer');
      if (this.release) this.release();
      if (this.opener && this.opener.setAttribute) {
        this.opener.setAttribute('aria-expanded', 'false');
        this.opener.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('modal-drawer', ModalDrawer);

  class MenuDrawer extends ModalDrawer {}
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Cart ---------- */
  class CartDrawer extends ModalDrawer {
    connectedCallback() {
      super.connectedCallback();
      bindCartControls(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const cartSections = () => {
    const ids = [];
    if ($('#CartDrawer')) ids.push(sectionIdOf($('#CartDrawer')) || 'cart-drawer');
    const page = $('[data-cart-page]');
    if (page) ids.push(sectionIdOf(page));
    return ids.filter(Boolean);
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      const wrap = document.getElementById(`shopify-section-${id}`);
      if (!wrap) return;
      if (id === sectionIdOf($('#CartDrawer') || document.body)) {
        const drawer = $('#CartDrawer');
        const fresh = $('#CartDrawer', doc);
        if (drawer && fresh) {
          ['.drawer__title', '.drawer__body', '.drawer__foot'].forEach((sel) => {
            const oldEl = $(sel, drawer);
            const newEl = $(sel, fresh);
            if (oldEl && newEl) oldEl.replaceWith(newEl);
            else if (oldEl && !newEl) oldEl.remove();
            else if (!oldEl && newEl) $('.drawer__panel', drawer).append(newEl);
          });
          bindCartControls(drawer);
        }
      } else {
        const fresh = $(`#shopify-section-${id}`, doc) || doc.body;
        wrap.innerHTML = fresh.innerHTML;
        bindCartControls(wrap);
      }
    });
  };

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bump');
      void el.offsetWidth;
      if (count > 0) el.classList.add('is-bump');
    });
    $$('[data-cart-count-label]').forEach((el) => { el.textContent = `${count}`; });
  };

  const cartFetch = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || FE.strings.cartError);
    return data;
  };

  const refreshCount = async () => {
    try {
      const cart = await (await fetch(`${FE.routes.cart}.js`)).json();
      updateCount(cart.item_count);
    } catch (e) { /* ignore */ }
  };

  const changeLine = async (line, quantity, el) => {
    const row = el && el.closest('.cart-line');
    if (row) row.classList.add('is-updating');
    try {
      const data = await cartFetch(`${FE.routes.cartChange}.js`, { line, quantity, sections: cartSections(), sections_url: window.location.pathname });
      renderCartSections(data.sections);
      updateCount(data.item_count);
    } catch (err) {
      if (row) row.classList.remove('is-updating');
      toast(err.message);
    }
  };

  function bindCartControls(scope) {
    if (scope._cartBound) return;
    scope._cartBound = true;
    scope.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-remove]');
      if (remove) { e.preventDefault(); changeLine(Number(remove.dataset.remove), 0, remove); }
    });
    scope.addEventListener('change', debounce((e) => {
      const input = e.target.closest('.qty__input[data-line]');
      if (input) changeLine(Number(input.dataset.line), Math.max(0, parseInt(input.value, 10) || 0), input);
      const note = e.target.closest('[data-cart-note]');
      if (note) fetch(`${FE.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
    }, 350));
  }
  document.addEventListener('DOMContentLoaded', () => { const page = $('[data-cart-page]'); if (page) bindCartControls(page.closest('.shopify-section') || page); });

  const openCart = (opener) => {
    const drawer = $('#CartDrawer');
    if (drawer && FE.cartType === 'drawer') { drawer.open(opener); return true; }
    return false;
  };

  const addToCart = async (formData, button) => {
    const label = button && (button.querySelector('[data-add-label]') || button);
    const original = label ? label.textContent : '';
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); }
    const errorEl = button && button.form && $('[data-form-error]', button.form);
    if (errorEl) errorEl.hidden = true;
    try {
      const sections = cartSections();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const res = await fetch(`${FE.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || FE.strings.cartError);
      renderCartSections(data.sections);
      await refreshCount();
      if (!openCart(button)) {
        if (FE.cartType === 'page') window.location.href = FE.routes.cart;
        else toast(FE.strings.added);
      }
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      else toast(err.message);
    } finally {
      if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); }
      if (label) label.textContent = original;
    }
  };

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form.matches('[data-product-form]') || form.matches('[data-quick-add]'))) return;
    if (!window.fetch) return;
    e.preventDefault();
    const submitter = e.submitter || $('[type="submit"]', form);
    const fd = new FormData(form, submitter && submitter.name ? submitter : undefined);
    if (!fd.get('id') && submitter && submitter.name === 'id') fd.set('id', submitter.value);
    addToCart(fd, submitter && submitter.closest('form') === form ? submitter : $('[data-add-button]', form) || submitter);
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        const step = Number(this.input.step) || 1;
        const min = this.input.min === '' ? 0 : Number(this.input.min);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + Number(btn.dataset.step) * step));
        if (next === Number(this.input.value)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Global openers ---------- */
  document.addEventListener('click', (e) => {
    const cartBtn = e.target.closest('[data-open-cart]');
    if (cartBtn && openCart(cartBtn)) { e.preventDefault(); return; }
    const menuBtn = e.target.closest('[data-open-menu]');
    if (menuBtn) { e.preventDefault(); const d = $('#MenuDrawer'); if (d) d.open(menuBtn); return; }
    const searchBtn = e.target.closest('[data-open-search]');
    if (searchBtn) { const s = $('#SearchOverlay'); if (s) { e.preventDefault(); s.open(searchBtn); } return; }
    const filterBtn = e.target.closest('[data-open-filters]');
    if (filterBtn) { e.preventDefault(); const d = document.getElementById(filterBtn.getAttribute('aria-controls')); if (d) d.open(filterBtn); return; }
    const guideBtn = e.target.closest('[data-open-size-guide]');
    if (guideBtn) { e.preventDefault(); const d = document.getElementById(guideBtn.getAttribute('aria-controls')); if (d) d.open(guideBtn); return; }
    const copy = e.target.closest('[data-copy-link]');
    if (copy && navigator.clipboard) { e.preventDefault(); navigator.clipboard.writeText(copy.dataset.copyLink).then(() => toast(FE.strings.linkCopied)); }
  });

  /* ---------- Announcement ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__msg', this);
      if (this.items.length < 2 || reduceMotion) return;
      this.i = 0;
      const tick = () => {
        this.items[this.i].classList.remove('is-active');
        this.i = (this.i + 1) % this.items.length;
        this.items[this.i].classList.add('is-active');
      };
      this.timer = setInterval(tick, Number(this.dataset.interval) || 5000);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => { this.timer = setInterval(tick, Number(this.dataset.interval) || 5000); });
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      this.last = window.scrollY;
      const setVars = () => {
        const ann = $('.announcement');
        document.documentElement.style.setProperty('--announcement-h', `${ann ? ann.offsetHeight : 0}px`);
        document.documentElement.style.setProperty('--header-h', `${$('.header', this).offsetHeight}px`);
      };
      setVars();
      window.addEventListener('resize', debounce(setVars, 150));
      this.onScroll = () => {
        const y = window.scrollY;
        this.classList.toggle('is-scrolled', y > 10);
        if (this.mode === 'scroll-up') {
          const hide = y > this.last && y > 240 && !this.contains(document.activeElement) && !$('details[open]', this);
          this.classList.toggle('is-hidden', hide);
        }
        this.last = y;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop menu disclosures ---------- */
  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = $('details', this);
      this.summary = $('summary', this);
      const hover = window.matchMedia('(hover: hover) and (min-width: 990px)');
      this.addEventListener('mouseenter', () => { if (hover.matches) { clearTimeout(this.t); this.closeOthers(); this.details.open = true; } });
      this.addEventListener('mouseleave', () => { if (hover.matches) this.t = setTimeout(() => { this.details.open = false; }, 140); });
      this.details.addEventListener('toggle', () => { if (this.details.open) this.closeOthers(); this.summary.setAttribute('aria-expanded', this.details.open); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.details.open) { this.details.open = false; this.summary.focus(); } });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.details.open = false; });
    }
    closeOthers() {
      $$('details-disclosure details[open]').forEach((d) => { if (d !== this.details) d.open = false; });
    }
  }
  customElements.define('details-disclosure', DetailsDisclosure);

  /* ---------- Search overlay + predictive search ---------- */
  class SearchOverlay extends HTMLElement {
    connectedCallback() {
      this.panel = $('.search-overlay__panel', this);
      this.input = $('input[type="search"]', this);
      this.results = $('.predictive', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      if (this.input && this.input.hasAttribute('data-predictive') && FE.routes.predictiveSearch) {
        this.input.addEventListener('input', debounce(() => this.search(), 260));
      }
    }
    open(opener) {
      this.opener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-drawer');
      this.release = trapFocus(this.panel);
      setTimeout(() => this.input && this.input.focus(), 60);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!openDrawers) document.body.classList.remove('has-drawer');
      if (this.release) this.release();
      if (this.opener) this.opener.focus({ preventScroll: true });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${FE.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product,page,query,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: this.controller.signal });
        const data = await res.json();
        this.render(q, data.resources && data.resources.results ? data.resources.results : {});
      } catch (e) { /* aborted */ }
    }
    render(q, r) {
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const products = r.products || [];
      const side = [...(r.queries || []).map((x) => ({ title: x.text, url: x.url })), ...(r.collections || []).map((x) => ({ title: x.title, url: x.url })), ...(r.pages || []).map((x) => ({ title: x.title, url: x.url }))].slice(0, 8);
      const img = (p) => (p.image ? `<span class="media media--portrait"><img src="${esc(p.image)}${p.image.includes('?') ? '&' : '?'}width=360" alt="" loading="lazy"></span>` : '<span class="media media--portrait"></span>');
      const searchUrl = `${FE.routes.root.replace(/\/$/, '')}/search?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last`;
      if (!products.length && !side.length) { this.results.innerHTML = `<p class="label">${esc(FE.strings.viewAll.replace('__TERMS__', q))}</p>`; return; }
      this.results.innerHTML = `
        <div class="predictive__grid">
          <div>${side.length ? `<p class="label">${esc(FE.strings.suggestions)}</p><ul class="predictive__list" role="list">${side.map((s) => `<li><a href="${esc(s.url)}" class="link-underline">${esc(s.title)}</a></li>`).join('')}</ul>` : ''}</div>
          <div>${products.length ? `<p class="label">${esc(FE.strings.products)}</p><ul class="predictive__products" role="list">${products.map((p) => `<li><a href="${esc(p.url)}" class="predictive__product">${img(p)}<span>${esc(p.title)}</span><span class="price">${esc(p.price ? formatPrice(p.price) : '')}</span></a></li>`).join('')}</ul>` : ''}</div>
        </div>
        <a href="${searchUrl}" class="link-arrow predictive__all">${esc(FE.strings.viewAll.replace('__TERMS__', q))}</a>`;
    }
  }
  const formatPrice = (p) => {
    const n = Number(p);
    if (Number.isNaN(n)) return p;
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(n); } catch (e) { return p; }
  };
  customElements.define('search-overlay', SearchOverlay);

  /* ---------- Slider rail ---------- */
  class SliderRail extends HTMLElement {
    connectedCallback() {
      this.track = $('.rail__track', this);
      this.prev = $('[data-rail-prev]', this);
      this.next = $('[data-rail-next]', this);
      this.bar = $('.rail__progress', this);
      if (!this.track || !this.prev) return;
      const page = () => this.track.clientWidth * 0.9;
      this.prev.addEventListener('click', () => this.track.scrollBy({ left: -page(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.next.addEventListener('click', () => this.track.scrollBy({ left: page(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const visible = this.track.clientWidth / this.track.scrollWidth;
        const pos = max > 0 ? this.track.scrollLeft / max : 1;
        if (this.bar) this.bar.style.setProperty('--progress', Math.min(1, visible + pos * (1 - visible)).toFixed(3));
        this.prev.disabled = this.track.scrollLeft < 4;
        this.next.disabled = this.track.scrollLeft > max - 4;
        const controls = $('.rail__controls', this);
        if (controls) controls.hidden = max <= 4;
      };
      this.track.addEventListener('scroll', debounce(update, 40), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    }
  }
  customElements.define('slider-rail', SliderRail);

  /* ---------- Product media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('.gallery-pdp__track', this);
      this.dots = $$('.gallery-pdp__dots span', this);
      if (this.dots.length) {
        this.track.addEventListener('scroll', debounce(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
        }, 50), { passive: true });
      }
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (!z) return;
        const viewer = $('zoom-viewer', this.closest('section'));
        if (viewer) viewer.show(z.dataset.zoom, z);
      });
    }
    showMedia(id) {
      const item = $(`[data-media-id="${id}"]`, this);
      if (!item) return;
      $$('.gallery-pdp__item', this).forEach((el) => el.classList.toggle('is-current', el === item));
      if (window.matchMedia('(min-width: 990px)').matches) {
        // Move the variant image to the top of the editorial grid.
        this.track.prepend(item);
      } else {
        this.track.scrollTo({ left: item.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class ZoomViewer extends HTMLElement {
    connectedCallback() {
      this.img = $('img', this);
      this.addEventListener('click', () => this.hide());
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hide(); });
    }
    show(src, opener) {
      this.opener = opener;
      this.img.src = src;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-drawer');
      $('[data-close]', this).focus();
    }
    hide() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      if (!openDrawers) document.body.classList.remove('has-drawer');
      if (this.opener) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('zoom-viewer', ZoomViewer);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this).textContent);
      this.section = this.closest('section');
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return $$('fieldset', this).map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
    }
    onChange() {
      const opts = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      $$('fieldset', this).forEach((fs, i) => { const s = $('[data-selected-value]', fs); if (s) s.textContent = opts[i]; });
      const idInput = $('[data-variant-id]', this.section);
      const buttons = $$('[data-add-button], [data-sticky-add]', this.section);
      const setLabel = (txt, disabled) => buttons.forEach((b) => { b.disabled = disabled; const l = $('[data-add-label]', b); if (l) l.textContent = txt; });
      if (!variant) { setLabel(FE.strings.unavailable, true); if (idInput) idInput.disabled = true; return; }
      if (idInput) { idInput.value = variant.id; idInput.disabled = !variant.available; }
      setLabel(variant.available ? FE.strings.addToCart : FE.strings.soldOut, !variant.available);
      const sticky = $('[data-sticky-variant]', this.section);
      if (sticky) sticky.textContent = variant.title;
      const installments = $('#ProductInstallments input[name="id"]', this.section);
      if (installments) installments.value = variant.id;
      if (variant.featured_media) { const g = $('media-gallery', this.section); if (g) g.showMedia(variant.featured_media.id); }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      this.refresh(variant.id);
    }
    async refresh(variantId) {
      const sectionId = this.dataset.section;
      const base = this.closest('product-info').dataset.url;
      try {
        const html = await (await fetch(`${base}?variant=${variantId}&section_id=${sectionId}`)).text();
        const doc = parseHTML(html);
        const newPrice = $('[data-price]', doc);
        const oldPrice = $('[data-price]', this.section);
        if (newPrice && oldPrice) oldPrice.innerHTML = newPrice.innerHTML;
        // Update availability marks on option values without losing focus.
        $$('fieldset', doc).forEach((fs, i) => {
          const current = $$('fieldset', this)[i];
          if (!current) return;
          $$('label', fs).forEach((label, n) => {
            const target = $$('label', current)[n];
            if (target) target.classList.toggle('is-unavailable', label.classList.contains('is-unavailable'));
          });
        });
      } catch (e) { /* keep client-side state */ }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductInfo extends HTMLElement {}
  customElements.define('product-info', ProductInfo);

  /* ---------- Sticky add to cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = $('[data-product-form]', this.closest('section'));
      if (!target || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      const footer = $('.footer');
      let formVisible = true;
      let footerVisible = false;
      const update = () => this.classList.toggle('is-visible', !formVisible && !footerVisible && target.getBoundingClientRect().top < 0);
      new IntersectionObserver(([e]) => { formVisible = e.isIntersecting; update(); }).observe(target);
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; update(); }).observe(footer);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const html = await (await fetch(this.dataset.url)).text();
          const fresh = $('product-recommendations', parseHTML(html));
          if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; observeReveal(this); }
          else this.closest('.shopify-section').hidden = true;
        } catch (e) { /* ignore */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection / search filters ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = $('[data-facet-form]', this);
      const sort = $('[data-sort]', this);
      if (this.form) this.form.addEventListener('input', debounce(() => this.submit(), 450));
      if (sort) sort.addEventListener('change', () => this.submit());
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (!link || !link.href) return;
        e.preventDefault();
        this.load(link.href);
      });
      const container = this.closest('#ProductGridContainer');
      if (container && !container._pagerBound) {
        container._pagerBound = true;
        container.addEventListener('click', (e) => {
          const link = e.target.closest('.pagination [data-facet-link], .empty-state [data-facet-link]');
          const current = $('facet-filters', container);
          if (!link || !current) return;
          e.preventDefault();
          current.load(link.href, !!link.closest('.pagination'));
        });
      }
    }
    submit() {
      const params = new URLSearchParams(new FormData(this.form));
      const sort = $('[data-sort]', this);
      if (sort) params.set('sort_by', sort.value);
      // Drop empty price inputs.
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      this.load(`${window.location.pathname}?${params.toString()}`);
    }
    async load(href, scroll) {
      const url = new URL(href, window.location.origin);
      const sectionId = this.dataset.section;
      const container = document.getElementById('ProductGridContainer');
      if (container) container.classList.add('is-loading-results');
      url.searchParams.set('section_id', sectionId);
      try {
        const html = await (await fetch(url.toString())).text();
        const fresh = $('#ProductGridContainer', parseHTML(html));
        const drawerWasOpen = $('modal-drawer.is-open', this);
        const active = document.activeElement;
        const focusKey = active && active.name ? `[name="${CSS.escape(active.name)}"]${active.type === 'checkbox' ? `[value="${CSS.escape(active.value)}"]` : ''}` : null;
        if (fresh && container) {
          // Preserve the open filter drawer while results update.
          const freshDrawer = $('modal-drawer', fresh);
          const currentDrawer = $('modal-drawer', this);
          if (drawerWasOpen && freshDrawer && currentDrawer) {
            $('.drawer__body', currentDrawer).replaceWith($('.drawer__body', freshDrawer));
            $('.drawer__foot', currentDrawer).replaceWith($('.drawer__foot', freshDrawer));
            freshDrawer.replaceWith(currentDrawer);
          }
          container.replaceChildren(...fresh.childNodes);
          observeReveal(container);
          if (drawerWasOpen && focusKey) { const f = $(`modal-drawer ${focusKey}`, container); if (f) f.focus({ preventScroll: true }); }
        }
        url.searchParams.delete('section_id');
        window.history.replaceState({}, '', url.toString());
        if (scroll && container) container.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      } catch (e) {
        window.location.href = href;
      } finally {
        if (container) container.classList.remove('is-loading-results');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- Account helpers ---------- */
  class AccountLogin extends HTMLElement {
    connectedCallback() {
      const login = $('[data-login-panel]', this);
      const recover = $('[data-recover-panel]', this);
      const show = (r) => { login.hidden = r; recover.hidden = !r; };
      if (window.location.hash === '#recover' || $('[data-recover-panel] .form-msg', this)) show(true);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-show-recover]')) show(true);
        if (e.target.closest('[data-hide-recover]')) show(false);
      });
    }
  }
  customElements.define('account-login', AccountLogin);

  class CustomerAddresses extends HTMLElement {
    connectedCallback() {
      $$('form', this).forEach((form) => {
        const country = $('[data-country-select]', form);
        const province = $('[data-province-select]', form);
        if (!country || !province) return;
        if (country.dataset.default) country.value = country.dataset.default;
        const fill = () => {
          const opt = country.options[country.selectedIndex];
          const list = opt && opt.dataset.provinces ? JSON.parse(opt.dataset.provinces) : [];
          province.innerHTML = list.map(([v, l]) => `<option value="${v}">${l}</option>`).join('');
          province.closest('[data-province-wrap]').hidden = !list.length;
          if (province.dataset.default) province.value = province.dataset.default;
        };
        country.addEventListener('change', () => { province.dataset.default = ''; fill(); });
        fill();
      });
      this.addEventListener('submit', (e) => {
        const msg = e.target.dataset.confirm;
        if (msg && !window.confirm(msg)) e.preventDefault();
      });
    }
  }
  customElements.define('customer-addresses', CustomerAddresses);

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  function observeReveal(scope = document) {
    const items = $$('[data-reveal]:not(.is-revealed)', scope);
    if (!document.body.classList.contains('has-reveal') || reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); revealObserver.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    items.forEach((el) => revealObserver.observe(el));
  }
  document.addEventListener('DOMContentLoaded', () => observeReveal());
  document.addEventListener('shopify:section:load', (e) => observeReveal(e.target));
  // Failsafe: never leave content hidden.
  window.addEventListener('load', () => setTimeout(() => $$('[data-reveal]').forEach((el) => { if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed'); }), 1200));

  /* ---------- Theme editor support ---------- */
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = $('cart-drawer', e.target);
    if (drawer) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = $('cart-drawer', e.target);
    if (drawer) drawer.close();
  });
})();

/* ---------- Oct 2026 updates: tabs, size guide link, colour-filtered gallery ---------- */
(() => {
  'use strict';
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  // Tabs for Model & fit and the FAQ page.
  class SimpleTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    }
    select(tab) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }
  if (!customElements.get('model-tabs')) customElements.define('model-tabs', class extends SimpleTabs {});
  if (!customElements.get('faq-tabs')) customElements.define('faq-tabs', class extends SimpleTabs {});

  // "Size guide" links without aria-controls open the product's size guide drawer.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-open-size-guide]:not([aria-controls])');
    if (!link) return;
    const d = document.querySelector('modal-drawer[id^="SizeGuide-"]');
    if (d && typeof d.open === 'function') { e.preventDefault(); e.stopPropagation(); d.open(link); }
  }, true);

  // Show only the photos of the selected colour (matched on image alt text).
  const Gallery = customElements.get('media-gallery');
  if (Gallery && !Gallery.prototype.filterByColor) {
    Gallery.prototype.filterByColor = function (color) {
      if (!color) return;
      const c = color.toLowerCase();
      const items = $$('.gallery-pdp__item', this);
      const matches = window.FEPick ? window.FEPick(items, c, (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1]) : items.filter((el) => (el.dataset.alt || '').includes(c));
      items.forEach((el) => { el.hidden = matches.length > 0 && !matches.includes(el); });
      const visible = items.filter((el) => !el.hidden);
      this.dataset.count = visible.length;
      $$('.gallery-pdp__dots span', this).forEach((d, n) => { d.hidden = n >= visible.length; d.classList.toggle('is-active', n === 0); });
      const track = $('.gallery-pdp__track', this);
      if (track) track.scrollTo({ left: 0 });
    };
  }
  document.addEventListener('change', (e) => {
    const picker = e.target.closest('variant-picker');
    if (!picker) return;
    const section = picker.closest('section');
    const g = section && $('media-gallery', section);
    if (!g || typeof g.filterByColor !== 'function') return;
    const ci = Number(g.dataset.colorIndex);
    if (!(ci >= 0)) return;
    const fs = $$('fieldset', picker)[ci];
    const checked = fs && $('input:checked', fs);
    if (checked) g.filterByColor(checked.value);
  });
})();

/* Long product info: stick by its bottom edge so every tab stays reachable while scrolling the gallery. */
(() => {
  const fit = () => {
    document.querySelectorAll('.product__sticky').forEach((el) => {
      if (window.innerWidth < 990) { el.style.top = ''; return; }
      const h = el.offsetHeight;
      const vh = window.innerHeight;
      el.style.top = h > vh - 120 ? (vh - h - 24) + 'px' : '';
    });
  };
  window.addEventListener('load', fit);
  window.addEventListener('resize', fit);
  document.addEventListener('toggle', fit, true);
  document.addEventListener('DOMContentLoaded', fit);
})();

/* ---------- Oct 2026 round 4: quick view, card swatches, size guide units ---------- */
(() => {
  'use strict';
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  // Product card colour swatches swap the card image and link.
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.card__swatch');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.card');
    if (!card) return;
    $$('.card__swatch', card).forEach((b) => b.classList.toggle('is-active', b === sw));
    const img = $('.card__img--primary', card);
    if (img && sw.dataset.swapSrc) {
      img.srcset = sw.dataset.swapSrcset || '';
      img.src = sw.dataset.swapSrc;
      card.classList.remove('card--has-hover');
    }
    if (sw.dataset.swapUrl) $$('a[href]', card).forEach((a) => { if (!a.closest('.card__swatches')) a.href = sw.dataset.swapUrl; });
    $$('[data-quick-view]', card).forEach((b) => { if (sw.dataset.swapUrl) b.dataset.quickView = sw.dataset.swapUrl; });
  });

  // Quick view drawer.
  let qv;
  const closeIcon = () => { const i = $('[data-close] .icon'); return i ? i.outerHTML : '&times;'; };
  const ensureDrawer = () => {
    if (qv) return qv;
    qv = document.createElement('modal-drawer');
    qv.className = 'drawer drawer--right color-scheme-ivory qv-drawer';
    qv.id = 'QuickView';
    qv.setAttribute('aria-hidden', 'true');
    qv.innerHTML = '<div class="drawer__overlay" data-close></div><div class="drawer__panel" role="dialog" aria-modal="true" aria-label="Quick view" tabindex="-1"><div class="drawer__head"><span class="drawer__title">Quick view</span><button type="button" class="icon-btn" data-close aria-label="Close">' + closeIcon() + '</button></div><div class="drawer__body" data-qv-body></div></div>';
    document.body.appendChild(qv);
    return qv;
  };
  const money = (cents, root) => {
    try { return new Intl.NumberFormat(root.dataset.locale || 'en', { style: 'currency', currency: root.dataset.currency || 'USD' }).format(cents / 100); }
    catch (err) { return '$' + (cents / 100).toFixed(2); }
  };
  const initQuickView = (root) => {
    const variants = JSON.parse(($('[data-qv-variants]', root) || {}).textContent || '[]');
    const colorIndex = Number(root.dataset.colorIndex);
    const sets = $$('[data-qv-option]', root);
    const btn = $('[data-add-button]', root);
    const label = btn && $('[data-add-label]', btn);
    const strings = (window.FleurElize && window.FleurElize.strings) || {};
    const filter = (color) => {
      if (!color) return;
      const c = color.toLowerCase();
      const imgs = $$('.qv__img', root);
      const match = window.FEPick ? window.FEPick(imgs, c, root.dataset.handle) : imgs.filter((el) => (el.dataset.alt || '').includes(c));
      imgs.forEach((el) => { el.hidden = match.length > 0 && !match.includes(el); });
      const media = $('[data-qv-media]', root);
      if (media) media.scrollTo({ top: 0, left: 0 });
    };
    const update = () => {
      const opts = sets.map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
      sets.forEach((fs, i) => { const s = $('[data-qv-chosen]', fs); if (s) s.textContent = opts[i] || ''; });
      const v = variants.find((x) => x.options.every((o, i) => o === opts[i]));
      const idInput = $('[data-qv-id]', root);
      if (!v) { if (btn) btn.disabled = true; if (label) label.textContent = strings.unavailable || 'Unavailable'; return; }
      if (idInput) idInput.value = v.id;
      if (btn) btn.disabled = !v.available;
      if (label) label.textContent = v.available ? (strings.addToCart || 'Add to bag') : (strings.soldOut || 'Sold out');
      const price = $('[data-qv-price]', root);
      if (price) price.textContent = money(v.price, root);
      if (colorIndex >= 0) filter(opts[colorIndex]);
    };
    root.addEventListener('change', update);
    update();
  };
  const openQuickView = async (url, opener) => {
    const drawer = ensureDrawer();
    const body = $('[data-qv-body]', drawer);
    body.innerHTML = '<div class="qv-loading">Loading</div>';
    drawer.open(opener);
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('view', 'quickview');
      const html = await (await fetch(u.toString())).text();
      body.innerHTML = html;
      const root = $('[data-qv]', body);
      if (root) { root.dataset.handle = (u.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || ''; initQuickView(root); }
    } catch (err) {
      window.location.href = url;
    }
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-quick-view]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    openQuickView(b.dataset.quickView, b);
  });
  // Close quick view once the item is on its way to the bag.
  document.addEventListener('submit', (e) => {
    if (qv && qv.contains(e.target)) setTimeout(() => { if (qv.classList.contains('is-open')) qv.close(); }, 900);
  });

  // Size guide: inch / cm switch for measurement columns.
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sg-unit]');
    if (!b) return;
    const wrap = b.closest('.sg__panel');
    const table = wrap && $('[data-sg-table] table', wrap);
    $$('[data-sg-unit]', wrap).forEach((x) => { const on = x === b; x.classList.toggle('is-active', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    if (!table) return;
    const unit = b.dataset.sgUnit;
    const rows = $$('tr', table);
    if (!rows.length) return;
    const head = $$('th, td', rows[0]);
    const cols = head.map((c, i) => (/inch|\(in\)|\bcm\b/i.test(c.textContent) ? i : -1)).filter((i) => i >= 0);
    head.forEach((c) => {
      if (!c.dataset.orig) c.dataset.orig = c.innerHTML;
      c.innerHTML = unit === 'cm' ? c.dataset.orig.replace(/inches|inch/gi, 'cm') : c.dataset.orig;
    });
    rows.slice(1).forEach((r) => {
      $$('th, td', r).forEach((c, i) => {
        if (!cols.includes(i)) return;
        if (!c.dataset.orig) c.dataset.orig = c.textContent;
        c.textContent = unit === 'cm'
          ? c.dataset.orig.replace(/\d+(\.\d+)?/g, (n) => String(Math.round(parseFloat(n) * 2.54)))
          : c.dataset.orig;
      });
    });
  });
})();

/* Size guide links (including ones inside product text) open the side drawer instead of leaving the page,
   and Model & fit refreshes when a colourway has its own models. */
(() => {
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="/pages/size-guide"]');
    if (!a) return;
    const d = document.querySelector('modal-drawer[id^="SizeGuide-"]');
    if (d && typeof d.open === 'function') { e.preventDefault(); e.stopPropagation(); d.open(a); }
  }, true);

  document.addEventListener('change', (e) => {
    const picker = e.target.closest('variant-picker');
    if (!picker) return;
    const section = picker.closest('section');
    const info = picker.closest('product-info');
    const model = section && section.querySelector('.accordion--model');
    if (!info || !model) return;
    setTimeout(async () => {
      const idInput = section.querySelector('[data-variant-id]');
      if (!idInput || !idInput.value) return;
      try {
        const html = await (await fetch(`${info.dataset.url}?variant=${idInput.value}&section_id=${picker.dataset.section}`)).text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.accordion--model');
        if (fresh && fresh.innerHTML !== model.innerHTML) { const open = model.open; model.innerHTML = fresh.innerHTML; model.open = open; }
      } catch (err) { /* keep current */ }
    }, 50);
  });
})();

/* Colour-aware product photos: match by alt text, else by FE_MEDIA_COLORS (products whose photos have no alt text). */
(() => {
  window.FE_MEDIA_COLORS = {"yvette-high-waist-trouser":{"beige":"06070 06037 06076 06088 06758 06014 06045 06023 06031 06053 06009 06131_80b2f2d2 06396 06362 09119 06408 06412 06417 06421 06346 06351 06563","all":"09922 09791","gray":"05527 05532 05593 05598 06746 08968 05543 05553 05557 05580 05705_6fc47e6b 05627 05616 05683 05701 05630 05633 05638 05643 05681 09038 09030","white":"09722 06444 06502 06484 06491 06514 06739 06504 06506 06510 06691 06622 06670 06693 06697 06703 06708 09734"},"yvette-boat-neck-top":{"gray":"05302 09038 05281 05359 05370 05416 09058 05292 05527_6d8bdada 05423 05418 05489 05431 05440 05442 05470","all":"09915","beige":"05846 09188 05816 05884 05885 05872 05890 06023_d6150189 06229 06231 06244","white":"06449 09734 06460 06475 06471 06478 06514_7322a405 06648 06612 06614 06654 06618 06622_bd2061c4 09751"},"yvette-high-waist-pencil-skirt":{"beige":"05846_9f7d67cf 09558 05825 05831 05890_fb3c08ac 05896 09603 05904 05910 05912 05921 05923 06220 06223 06276 06281 06290 06294 06296 06298 06300 06312 06321","all":"09937","gray":"09066 09077 05315 05292_6f792bce 05323 08968 05356 05375 05377 05378 05382 05401 05411 05418_cfd8c516 05493 05497 05499 05505 05513 05517 05519 05431_9050f916 06110_12ac6839"},"yvette-structured-blazer":{"beige":"06152 09586 06186 06131 06138 06151 06179 09571 06560 06523 06526 09197 06563 09598","gray":"05705 05775 05724 05733 05753 05768 05770 05795 05930 05950 05993 05997 06002 05926_d41aa048 06106 05930_11fcdb91 05935_ecf03bb1 08996 09078_06b904cf","all":"09101"},"simone-high-waist-shorts":{"white":"04833 09689 04743 04740 06736_bc34c085 04748 04754 04760 04823 04773 05031 05075 05077 05096 05004","brown":"04657 04704 04714 04716 04720 06716 04724 04632 04917 04952 04955 04958 04963 04977 04905","all":"09384 09993"},"simone-double-breasted-blazer":{"white":"04773 04796 04823 04833 04857 04874 04879 09702 09703 04990 04995 05050 05002 05004 05010 05064 05071","brown":"09469 04606_da696ce7 04618 04632 04645 09421 04657 04671 04688 04697 09445 04889_f2f2fa11 04901 04903 04905 04917 04941 04944 04947","all":"09384 09993"},"sienna-cropped-v-neck-jacket":{"beige":"05127 05208 05143 05149 05197 05193 05150 05161 05167 05194 05200 05177 05184 09333 05248 05210 05269 09499 05241 05212 05213 05217 09791 05219 05220 05222 05238 09343 09329","all":"09262 09234","black":"08850 08830 08927 08787 08835 08826 08808 09327 08773 08837 08842 08802 08777 08780 08914 08924 08919 08907 08871 08877 08881 08872 08876 08868 08880"}};
  const key = (el) => {
    const img = el.querySelector('img');
    const src = img ? (img.getAttribute('src') || '') : '';
    return src.split('/').pop().split('?')[0].replace(/\.[a-z]+$/i, '').replace(/^ASHOTS-/i, '').replace(/_([0-9a-f]{8})-[0-9a-f-]+$/i, '_$1');
  };
  window.FEPick = (els, color, handle) => {
    const c = String(color || '').toLowerCase();
    let match = els.filter((el) => (el.dataset.alt || '').includes(c));
    const map = handle && window.FE_MEDIA_COLORS[decodeURIComponent(handle)];
    if (!match.length && map && map[c]) {
      const keys = new Set((map[c] + ' ' + (map.all || '')).split(' ').filter(Boolean));
      match = els.filter((el) => keys.has(key(el)));
    }
    match.forEach((el) => { const img = el.querySelector('img'); if (img) img.loading = 'eager'; });
    return match;
  };
  const initGalleries = () => {
    document.querySelectorAll('media-gallery[data-color-index]').forEach((g) => {
      const ci = Number(g.dataset.colorIndex);
      if (!(ci >= 0) || !g.filterByColor) return;
      const section = g.closest('section');
      const picker = section && section.querySelector('variant-picker');
      if (!picker || !picker.selectedOptions) return;
      const color = picker.selectedOptions()[ci];
      if (color) g.filterByColor(color);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initGalleries); else initGalleries();
})();

/* Model & Fit follows the chosen size; Shop menu click opens Shop all; collection links default to newest first. */
(() => {
  let lastSize = null;
  let userPicked = false;
  const syncModel = () => {
    const picker = document.querySelector('variant-picker');
    if (!picker || !picker.selectedOptions) return;
    const sets = [...picker.querySelectorAll('fieldset')];
    const idx = sets.findIndex((fs) => /size/i.test(((fs.querySelector('legend') || {}).textContent || '').split(':')[0]));
    if (idx < 0) return;
    const size = String(picker.selectedOptions()[idx] || '').trim().toLowerCase();
    if (!size) return;
    const sizeChanged = lastSize !== null && size !== lastSize;
    lastSize = size;
    document.querySelectorAll('model-tabs').forEach((mt) => {
      const panels = [...mt.querySelectorAll('.model-tabs__panel')];
      const i = panels.findIndex((p) => ((p.querySelector('.model-card__wears strong') || {}).textContent || '').trim().toLowerCase() === size);
      if (i < 0) return;
      const tabs = [...mt.querySelectorAll('[role="tab"]')];
      if (tabs[i] && typeof mt.select === 'function') mt.select(tabs[i]);
      else panels.forEach((p, n) => { p.hidden = n !== i; });
      if (userPicked && sizeChanged) { const acc = mt.closest('details'); if (acc) acc.open = true; }
    });
  };
  document.addEventListener('change', (e) => { if (e.target.closest('variant-picker')) { userPicked = true; setTimeout(syncModel, 0); } });
  const watchModels = () => document.querySelectorAll('.accordion--model').forEach((m) => {
    new MutationObserver(() => { const keep = lastSize; lastSize = null; syncModel(); if (lastSize === null) lastSize = keep; }).observe(m, { childList: true });
  });

  // Desktop: clicking a top-level menu item with a dropdown goes to its page (e.g. Shop -> Shop all); hover still opens the menu.
  document.addEventListener('click', (e) => {
    const sum = e.target.closest('summary[data-href]');
    if (!sum || !window.matchMedia('(hover: hover) and (min-width: 990px)').matches) return;
    e.preventDefault();
    window.location.href = sum.dataset.href;
  });

  // Collection pages always open newest first.
  const SORT = 'created-descending';
  const addSort = (href) => {
    try {
      const u = new URL(href, window.location.origin);
      if (u.origin !== window.location.origin || !/^\/collections\/[^/]+\/?$/.test(u.pathname) || u.searchParams.has('sort_by')) return null;
      u.searchParams.set('sort_by', SORT);
      return u.pathname + u.search + u.hash;
    } catch (err) { return null; }
  };
  const rewriteLinks = (root) => {
    (root || document).querySelectorAll('a[href*="/collections/"]').forEach((a) => { const h = addSort(a.getAttribute('href')); if (h) a.setAttribute('href', h); });
    (root || document).querySelectorAll('summary[data-href*="/collections/"]').forEach((s) => { const h = addSort(s.dataset.href); if (h) s.dataset.href = h; });
  };

  const init = () => { watchModels(); syncModel(); rewriteLinks(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* fix11: quick view shows the model who wears the chosen size; empty paragraphs in rich text are removed. */
(() => {
  const ALIAS = { 'extra small': 'xs', 'x-small': 'xs', small: 's', medium: 'm', large: 'l', 'extra large': 'xl', 'x-large': 'xl', xxl: '2xl', 'xx-large': '2xl', xxxl: '3xl' };
  const norm = (s) => { const v = String(s || '').trim().toLowerCase().replace(/\s+/g, ' '); return ALIAS[v] || v; };
  const sync = (root) => {
    const cards = [...root.querySelectorAll('.qv__model')];
    const set = root.querySelector('[data-qv-size]');
    const picked = set && set.querySelector('input:checked');
    if (!cards.length || !picked) return;
    const i = cards.findIndex((c) => norm(c.dataset.size) === norm(picked.value));
    if (i >= 0) cards.forEach((c, n) => { c.hidden = n !== i; });
  };
  const clean = (scope) => (scope || document).querySelectorAll('.rte p').forEach((p) => {
    if (!p.textContent.replace(/\u00a0/g, ' ').trim() && !p.querySelector('img, iframe, video, svg')) p.remove();
  });
  document.addEventListener('change', (e) => { const r = e.target.closest('[data-qv]'); if (r) sync(r); });
  new MutationObserver(() => {
    document.querySelectorAll('[data-qv]:not([data-qv-ready])').forEach((r) => { r.dataset.qvReady = '1'; sync(r); clean(r); });
  }).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => clean()); else clean();
})();

/* fix13: when no model wears the chosen size, show the model in the nearest size (ties go up a size, matching our size-up advice). */
(() => {
  const ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', '2xl', '3xl', '4xl'];
  const ALIAS = { 'extra small': 'xs', 'x-small': 'xs', small: 's', medium: 'm', large: 'l', 'extra large': 'xl', 'x-large': 'xl', xxl: '2xl', 'xx-large': '2xl', xxxl: '3xl' };
  const norm = (s) => { const v = String(s || '').trim().toLowerCase().replace(/\s+/g, ' '); return ALIAS[v] || v; };
  const nearest = (sizes, want) => {
    const w = ORDER.indexOf(norm(want));
    if (w < 0) return -1;
    let bestD = Infinity, bestIdx = -1;
    sizes.forEach((s, i) => {
      const k = ORDER.indexOf(norm(s));
      if (k < 0) return;
      const d = Math.abs(k - w) - (k > w ? 0.1 : 0);
      if (d < bestD) { bestD = d; bestIdx = i; }
    });
    return bestIdx;
  };
  window.FENearest = nearest;
  const syncPage = () => {
    const picker = document.querySelector('variant-picker');
    if (!picker || !picker.selectedOptions) return;
    const sets = [...picker.querySelectorAll('fieldset')];
    const idx = sets.findIndex((fs) => /size/i.test(((fs.querySelector('legend') || {}).textContent || '').split(':')[0]));
    if (idx < 0) return;
    const size = picker.selectedOptions()[idx];
    document.querySelectorAll('model-tabs').forEach((mt) => {
      const panels = [...mt.querySelectorAll('.model-tabs__panel')];
      const i = nearest(panels.map((p) => (p.querySelector('.model-card__wears strong') || {}).textContent || ''), size);
      if (i < 0) return;
      const tabs = [...mt.querySelectorAll('[role="tab"]')];
      if (tabs[i] && typeof mt.select === 'function') mt.select(tabs[i]);
      else panels.forEach((p, n) => { p.hidden = n !== i; });
    });
  };
  const syncQV = (root) => {
    const cards = [...root.querySelectorAll('.qv__model')];
    const picked = root.querySelector('[data-qv-size] input:checked');
    if (!cards.length || !picked) return;
    const i = nearest(cards.map((c) => c.dataset.size), picked.value);
    if (i >= 0) cards.forEach((c, n) => { c.hidden = n !== i; });
  };
  document.addEventListener('change', (e) => {
    const qv = e.target.closest('[data-qv]');
    if (qv) { setTimeout(() => syncQV(qv), 0); return; }
    if (e.target.closest('variant-picker')) setTimeout(syncPage, 30);
  });
  new MutationObserver(() => {
    document.querySelectorAll('[data-qv]:not([data-qv-near])').forEach((r) => { r.dataset.qvNear = '1'; syncQV(r); });
  }).observe(document.body, { childList: true, subtree: true });
  const init = () => {
    document.querySelectorAll('.accordion--model').forEach((m) => new MutationObserver(() => setTimeout(syncPage, 0)).observe(m, { childList: true }));
    syncPage();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* fix20: quick view puts photos of the model who wears the chosen size first.
   Matching: model name in alt text > FE_MEDIA_MODELS (photos sorted by model, keyed by file name) > "xl_" file names = plus-size model. */
(() => {
  window.FE_MEDIA_MODELS = {"sienna-cropped-v-neck-jacket":{"liz":"05127 05143 05149 05197 05193 05150 05161 05167 05194 05200 05177 05184 09499 09791 08830 08787 08835 08826 08808 08773 08837 08842 08802 08777 08780","chelsea":"05208 09333 05248 05210 05269 05241 05212 05213 05217 09791 05219 05220 05222 05238 09343 09262 09329 08850 08927 09234 08914 08924 08919 08907 08871 08877 08881 08872 08876 08868 08880","iylan":"09262 09234 09327"},"simone-double-breasted-blazer":{"liz":"04773 04796 04823 04833 04857 04874 04879 09469 04606_da696ce7 04618 04632 04645 04657 04671 04688 04697","chelsea":"04990 04995 05050 05002 05004 05010 05064 05071 04889_f2f2fa11 04901 04903 04905 04917 04941 04944 04947","iylan":"09702 09703"},"simone-high-waist-shorts":{"liz":"04833 04743 04740 04748 04754 04760 04823 04773 04657 04704 04714 04716 04720 04724 04632","chelsea":"05031 05075 05077 05096 05004 04917 04952 04955 04958 04963 04977 04905","iylan":"09689"},"yvette-structured-blazer":{"liz":"06152 06186 06131 06138 06151 06179 09197 05705 05775 05724 05733 05753 05768 05770 05795 08996 09078_06b904cf","chelsea":"06560 06523 06526 09197 06563 05930 05950 05993 05997 06002 05926_d41aa048 06106 05930_11fcdb91 05935_ecf03bb1","erika":"09586 09571 09598","iylan":"08996"},"yvette-high-waist-trouser":{"liz":"06070 06037 06076 06088 06014 06045 06023 06031 06053 06009 06131_80b2f2d2 09119 05527 05532 05593 05598 08968 05543 05553 05557 05580 05705_6fc47e6b 09722 06444 06502 06484 06491 06514 06504 06506 06510 09791 09734","chelsea":"06396 06362 09119 06408 06412 06417 06421 06346 06351 06563 05627 05616 05683 05701 05630 05633 05638 05643 05681 09791 06691 06622 06670 06693 06697 06703 06708","iylan":"08968 09038 09030"},"yvette-boat-neck-top":{"liz":"05302 05281 05359 05370 05292 05527_6d8bdada 05846 09188 05816 05884 05885 05872 05890 06023_d6150189 06449 09734 06460 06475 06471 06478 06514_7322a405 09751","chelsea":"05416 05423 05418 05489 05431 05440 05442 05470 09188 06229 06231 06244 06648 06612 06614 06654 06618 06622_bd2061c4","iylan":"09038 09058"},"yvette-high-waist-pencil-skirt":{"liz":"05846_9f7d67cf 09558 05825 05831 05890_fb3c08ac 05896 05904 05910 05912 05921 05923 09066 09077 05315 05292_6f792bce 05323 08968 05356 05375 05377 05378 05382 05401 05411","chelsea":"06220 06223 06276 06281 06290 06294 06296 06298 06300 06312 06321 05418_cfd8c516 05493 05431_9050f916 06110_12ac6839","erika":"09603","iylan":"08968"},"isabella-flowy-tube-top-dress-with-detachable-shawl":{"liz":"09034 09018 09028 24 27 25 26 28 29 30 31 32 33 34 35 4 3 1 2 5 6 8 7 11 9 10 09155 12 16","chelsea":"36 41 37 38 39 40 42 43 44 45 46 47 48 13 14 15 17 18 19 20 21 22 23","venus":"025 049","anfisa":"09229 09257 09213 09221 09155"},"aurora-capsleeve-flowy-maxi-dress":{"venus":"08775 08744 08755 08823 09401","anfisa":"08823","liz":"49 54 52 50 51 53 55 09464 09474 09401","chelsea":"56 61 60 57 58 59 62 63 69 64 67 65 66 68"},"lorelei-shawl-collar-dress":{"anfisa":"08530 08575 08576 08538 08834 09177","venus":"08834","liz":"70 74 71 75 73 72 09193 09198 09190 117 121 118 119 120 122 123 09177","chelsea":"76 80 77 78 79 09201 87 91 88 89 90 92"},"rosalind-boat-neck-open-back-dress":{"liz":"83 85 116 114 111 112 113 115","chelsea":"93 98 97 94 95 96 99 104 100 101 102 103","venus":"09274 09325 09319 09302 09401"}};
  const PLUS = ['xl', '2xl', '3xl', '4xl'];
  const imgOf = (el) => el.querySelector('img');
  const fileOf = (el) => { const i = imgOf(el); return ((i && (i.getAttribute('src') || i.currentSrc)) || '').split('/').pop().split('?')[0]; };
  const keyOf = (el) => fileOf(el).replace(/\.[a-z]+$/i, '').replace(/^ASHOTS-/i, '').replace(/_([0-9a-f]{8})-[0-9a-f-]+$/i, '_$1');
  const score = (el, model, handle) => {
    const name = model.name.toLowerCase().trim();
    const alt = ((el.dataset && el.dataset.alt) || (imgOf(el) || {}).alt || '').toLowerCase();
    if (name && alt.includes(name)) return 3;
    const map = window.FE_MEDIA_MODELS[handle];
    if (map) return (map[name] || '').split(' ').includes(keyOf(el)) ? 3 : 0;
    const isXL = /(^|[_-])xl[_-]|plus/i.test(fileOf(el));
    if (PLUS.includes(model.size)) return isXL ? 2 : 0;
    return isXL ? 0 : 1;
  };
  const reorder = (items, model, handle) => {
    if (!items.length || !model || !model.name) return false;
    const parent = items[0].parentNode;
    const scored = items.map((el, i) => ({ el, i: Number(el.dataset.qvIdx || i), s: score(el, model, handle) }));
    if (scored.every((x) => x.s === scored[0].s)) return false;
    scored.sort((a, b) => (b.s - a.s) || (a.i - b.i)).forEach((x) => parent.appendChild(x.el));
    return true;
  };
  const qvModel = (root) => {
    const c = root.querySelector('.qv__model:not([hidden])');
    return c ? { name: (c.querySelector('.model-card__name') || {}).textContent || '', size: String(c.dataset.size || '').toLowerCase() } : null;
  };
  const sync = (root) => {
    const media = root.querySelector('[data-qv-media]');
    if (!media) return;
    const items = [...media.querySelectorAll('.qv__img')];
    items.forEach((el, i) => { if (el.dataset.qvIdx == null) el.dataset.qvIdx = i; });
    if (reorder(items, qvModel(root), root.dataset.handle || '')) media.scrollTo({ top: 0, left: 0 });
  };
  document.addEventListener('change', (e) => { const qv = e.target.closest('[data-qv]'); if (qv) setTimeout(() => sync(qv), 60); });
  new MutationObserver(() => {
    document.querySelectorAll('[data-qv]:not([data-qv-order])').forEach((r) => { r.dataset.qvOrder = '1'; setTimeout(() => sync(r), 120); });
  }).observe(document.body, { childList: true, subtree: true });
})();
